import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Trophy, Flame, Zap, BookOpen, Star, Shield, Target, Award,
  TrendingUp, ClipboardList, CheckCircle, ChevronRight,
  Cookie, Key, Eye, EyeOff, LogOut, Compass, ChevronDown, ChevronUp,
  Briefcase, Download, RefreshCw, AlertCircle, FileText, Globe,
  Lock, Share2, QrCode, Plus, X, Wand2, GraduationCap,
} from 'lucide-react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useCookieConsent } from '../context/CookieConsentContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const BADGE_META = {
  explorateur:      { icon: Compass,   label: 'Explorateur',      desc: 'Premier cours commencé',  color: '#ff6b6b' },
  premier_quiz:     { icon: Star,      label: 'Premier Quiz',     desc: 'Premier quiz passé',      color: '#ffc107' },
  perfectionniste:  { icon: Target,    label: 'Perfectionniste',  desc: 'Score parfait',           color: '#20c997' },
  apprenti:         { icon: BookOpen,  label: 'Apprenti',         desc: '100 XP gagnés',           color: '#3b82f6' },
  studieux:         { icon: TrendingUp,label: 'Studieux',         desc: '500 XP gagnés',           color: '#a855f7' },
  expert:           { icon: Award,     label: 'Expert',           desc: '1000 XP gagnés',          color: '#ffc107' },
  serie_feu:        { icon: Flame,     label: 'Flamme de feu',    desc: '7 jours consécutifs',     color: '#ff6b6b' },
  bilan_complete:   { icon: ClipboardList, label: 'Bilan complété', desc: 'Bilan Kurso terminé',  color: '#20c997' },
};

const LEVELS = [
  { min: 0,    label: 'lvlBeginner',    color: '#e8e8e8' },
  { min: 100,  label: 'lvlApprentice',  color: '#20c997' },
  { min: 250,  label: 'lvlIntermediate',color: '#3b82f6' },
  { min: 500,  label: 'lvlAdvanced',    color: '#ffc107' },
  { min: 1000, label: 'lvlExpert',      color: '#ff6b6b' },
  { min: 2000, label: 'lvlMaster',      color: '#a855f7' },
  { min: 5000, label: 'lvlLegend',      color: '#ffc107' },
];

function getLevel(xp) {
  for (let i = LEVELS.length - 1; i >= 0; i--) {
    if (xp >= LEVELS[i].min) return LEVELS[i];
  }
  return LEVELS[0];
}

// ── CV Complet ────────────────────────────────────────────────────────────────
function CVDataSection() {
  const [cvData, setCvData] = useState({
    title: '', phone: '', location: '', linkedin: '', photo_url: '',
    summary: '', experiences: [], formations: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showExpForm, setShowExpForm] = useState(false);
  const [showFormForm, setShowFormForm] = useState(false);
  const [newExp, setNewExp] = useState({ title: '', company: '', period: '', description: '' });
  const [newForm, setNewForm] = useState({ diploma: '', school: '', year: '', description: '' });

  useEffect(() => {
    axios.get(`${API}/users/cv-data`, { withCredentials: true })
      .then(res => {
        const d = res.data || {};
        setCvData({
          title: d.title || '',
          phone: d.phone || '',
          location: d.location || '',
          linkedin: d.linkedin || '',
          photo_url: d.photo_url || '',
          summary: d.summary || '',
          experiences: d.experiences || [],
          formations: d.formations || [],
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/users/cv-data`, cvData, { withCredentials: true });
      toast.success('CV sauvegardé');
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateSummary = async () => {
    setGenerating(true);
    try {
      await axios.patch(`${API}/users/cv-data`, cvData, { withCredentials: true });
      const res = await axios.post(`${API}/users/generate-summary`, {}, { withCredentials: true });
      setCvData(prev => ({ ...prev, summary: res.data.summary }));
      toast.success(`Résumé généré — ${res.data.credits_used} crédit utilisé`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la génération IA');
    } finally {
      setGenerating(false);
    }
  };

  const addExperience = () => {
    if (!newExp.title.trim() && !newExp.company.trim()) return;
    setCvData(prev => ({ ...prev, experiences: [...prev.experiences, { ...newExp }] }));
    setNewExp({ title: '', company: '', period: '', description: '' });
    setShowExpForm(false);
  };

  const removeExperience = (idx) => {
    setCvData(prev => ({ ...prev, experiences: prev.experiences.filter((_, i) => i !== idx) }));
  };

  const addFormation = () => {
    if (!newForm.diploma.trim() && !newForm.school.trim()) return;
    setCvData(prev => ({ ...prev, formations: [...prev.formations, { ...newForm }] }));
    setNewForm({ diploma: '', school: '', year: '', description: '' });
    setShowFormForm(false);
  };

  const removeFormation = (idx) => {
    setCvData(prev => ({ ...prev, formations: prev.formations.filter((_, i) => i !== idx) }));
  };

  if (loading) return <div className="animate-shimmer h-40 rounded-2xl" />;

  return (
    <div className="space-y-4">
      {/* Header + Save */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-lg font-semibold text-white flex items-center gap-2">
            <FileText size={16} className="text-[#ff6b6b]" /> Mon CV Complet
          </h2>
          <p className="text-xs text-[#a0a0ab] mt-0.5">Ces données enrichissent votre export "Format Complet"</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          data-testid="cv-save-btn"
          className="flex items-center gap-1.5 bg-[#ff6b6b] text-white px-4 py-2 rounded-xl text-sm font-bold hover:opacity-90 disabled:opacity-50 transition-opacity"
        >
          {saving ? <RefreshCw size={13} className="animate-spin" /> : <CheckCircle size={13} />}
          Sauvegarder
        </button>
      </div>

      {/* Informations de base */}
      <div className="kurso-card p-5 space-y-3">
        <h3 className="text-white font-semibold text-sm flex items-center gap-2">
          <Briefcase size={14} className="text-[#ffc107]" /> Informations professionnelles
        </h3>
        <div>
          <label className="text-xs text-[#a0a0ab] mb-1 block">Titre professionnel</label>
          <input
            value={cvData.title}
            onChange={e => setCvData(p => ({ ...p, title: e.target.value }))}
            placeholder="ex: Développeur Full Stack Senior"
            data-testid="cv-title-input"
            className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#ff6b6b]/50 transition-colors"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-[#a0a0ab] mb-1 block">Téléphone</label>
            <input
              value={cvData.phone}
              onChange={e => setCvData(p => ({ ...p, phone: e.target.value }))}
              placeholder="+33 6 00 00 00 00"
              data-testid="cv-phone-input"
              className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#ff6b6b]/50 transition-colors"
            />
          </div>
          <div>
            <label className="text-xs text-[#a0a0ab] mb-1 block">Localisation</label>
            <input
              value={cvData.location}
              onChange={e => setCvData(p => ({ ...p, location: e.target.value }))}
              placeholder="Paris, France"
              data-testid="cv-location-input"
              className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#ff6b6b]/50 transition-colors"
            />
          </div>
        </div>
        <div>
          <label className="text-xs text-[#a0a0ab] mb-1 block">LinkedIn (URL)</label>
          <input
            value={cvData.linkedin}
            onChange={e => setCvData(p => ({ ...p, linkedin: e.target.value }))}
            placeholder="https://linkedin.com/in/votre-profil"
            data-testid="cv-linkedin-input"
            className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#ff6b6b]/50 transition-colors"
          />
        </div>
        <div>
          <label className="text-xs text-[#a0a0ab] mb-1 block">Photo (URL optionnelle)</label>
          <input
            value={cvData.photo_url}
            onChange={e => setCvData(p => ({ ...p, photo_url: e.target.value }))}
            placeholder="https://example.com/photo.jpg"
            data-testid="cv-photo-input"
            className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#ff6b6b]/50 transition-colors"
          />
        </div>
      </div>

      {/* Résumé professionnel */}
      <div className="kurso-card p-5 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="text-white font-semibold text-sm flex items-center gap-2">
            <FileText size={14} className="text-[#20c997]" /> Résumé professionnel
          </h3>
          <button
            onClick={handleGenerateSummary}
            disabled={generating}
            data-testid="cv-generate-summary-btn"
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-[#20c997]/10 text-[#20c997] hover:bg-[#20c997]/20 border border-[#20c997]/20 disabled:opacity-50 transition-all"
          >
            {generating ? <RefreshCw size={11} className="animate-spin" /> : <Wand2 size={11} />}
            {generating ? 'Génération...' : 'Générer avec l\'IA (1 crédit)'}
          </button>
        </div>
        <textarea
          value={cvData.summary}
          onChange={e => setCvData(p => ({ ...p, summary: e.target.value }))}
          placeholder="Décrivez votre profil professionnel en 2-3 phrases percutantes..."
          rows={3}
          data-testid="cv-summary-textarea"
          className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#20c997]/50 resize-none transition-colors"
        />
      </div>

      {/* Expériences */}
      <div className="kurso-card p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-white font-semibold text-sm flex items-center gap-2">
            <Briefcase size={14} className="text-[#a78bfa]" /> Expériences professionnelles
          </h3>
          <button
            onClick={() => setShowExpForm(v => !v)}
            data-testid="cv-add-exp-btn"
            className="w-7 h-7 rounded-lg bg-[#a78bfa]/10 flex items-center justify-center text-[#a78bfa] hover:bg-[#a78bfa]/20 transition-colors"
          >
            {showExpForm ? <X size={13} /> : <Plus size={13} />}
          </button>
        </div>

        {cvData.experiences.length === 0 && !showExpForm && (
          <p className="text-xs text-[#64748b]">Aucune expérience ajoutée. Cliquez sur + pour commencer.</p>
        )}

        {cvData.experiences.map((exp, i) => (
          <div key={`exp-${exp.company || ''}-${exp.title || ''}-${i}`} className="bg-[#0f0e17] border border-white/5 rounded-xl p-3 flex items-start justify-between gap-2" data-testid={`cv-exp-item-${i}`}>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium">{exp.title}</p>
              <p className="text-[#a0a0ab] text-xs">{exp.company}{exp.period ? ` · ${exp.period}` : ''}</p>
              {exp.description && <p className="text-[#64748b] text-xs mt-1 line-clamp-2">{exp.description}</p>}
            </div>
            <button
              onClick={() => removeExperience(i)}
              data-testid={`cv-remove-exp-${i}`}
              className="w-6 h-6 rounded-lg flex items-center justify-center text-[#64748b] hover:text-[#ff6b6b] hover:bg-[#ff6b6b]/10 transition-colors shrink-0"
            >
              <X size={12} />
            </button>
          </div>
        ))}

        {showExpForm && (
          <div className="bg-[#0f0e17] border border-[#a78bfa]/20 rounded-xl p-3 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <input
                value={newExp.title}
                onChange={e => setNewExp(p => ({ ...p, title: e.target.value }))}
                placeholder="Intitulé du poste"
                data-testid="cv-exp-title-input"
                className="bg-white/5 border border-white/10 text-white rounded-lg px-2.5 py-2 text-xs focus:outline-none focus:border-[#a78bfa]/50"
              />
              <input
                value={newExp.company}
                onChange={e => setNewExp(p => ({ ...p, company: e.target.value }))}
                placeholder="Entreprise"
                data-testid="cv-exp-company-input"
                className="bg-white/5 border border-white/10 text-white rounded-lg px-2.5 py-2 text-xs focus:outline-none focus:border-[#a78bfa]/50"
              />
            </div>
            <input
              value={newExp.period}
              onChange={e => setNewExp(p => ({ ...p, period: e.target.value }))}
              placeholder="Période (ex: Jan 2021 – Déc 2023)"
              data-testid="cv-exp-period-input"
              className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-2.5 py-2 text-xs focus:outline-none focus:border-[#a78bfa]/50"
            />
            <textarea
              value={newExp.description}
              onChange={e => setNewExp(p => ({ ...p, description: e.target.value }))}
              placeholder="Description (optionnelle)"
              rows={2}
              data-testid="cv-exp-description-input"
              className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-2.5 py-2 text-xs focus:outline-none focus:border-[#a78bfa]/50 resize-none"
            />
            <div className="flex gap-2">
              <button
                onClick={addExperience}
                data-testid="cv-exp-add-confirm-btn"
                className="flex-1 py-2 rounded-lg bg-[#a78bfa] text-white text-xs font-bold hover:opacity-90 transition-opacity"
              >
                Ajouter
              </button>
              <button
                onClick={() => { setShowExpForm(false); setNewExp({ title: '', company: '', period: '', description: '' }); }}
                className="px-3 py-2 rounded-lg border border-white/10 text-[#a0a0ab] text-xs hover:text-white transition-colors"
              >
                Annuler
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Formations */}
      <div className="kurso-card p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-white font-semibold text-sm flex items-center gap-2">
            <GraduationCap size={14} className="text-[#20c997]" /> Formations académiques
          </h3>
          <button
            onClick={() => setShowFormForm(v => !v)}
            data-testid="cv-add-formation-btn"
            className="w-7 h-7 rounded-lg bg-[#20c997]/10 flex items-center justify-center text-[#20c997] hover:bg-[#20c997]/20 transition-colors"
          >
            {showFormForm ? <X size={13} /> : <Plus size={13} />}
          </button>
        </div>

        {cvData.formations.length === 0 && !showFormForm && (
          <p className="text-xs text-[#64748b]">Aucune formation ajoutée.</p>
        )}

        {cvData.formations.map((form, i) => (
          <div key={`form-${form.school || ''}-${form.diploma || ''}-${i}`} className="bg-[#0f0e17] border border-white/5 rounded-xl p-3 flex items-start justify-between gap-2" data-testid={`cv-formation-item-${i}`}>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium">{form.diploma}</p>
              <p className="text-[#a0a0ab] text-xs">{form.school}{form.year ? ` · ${form.year}` : ''}</p>
              {form.description && <p className="text-[#64748b] text-xs mt-1 line-clamp-2">{form.description}</p>}
            </div>
            <button
              onClick={() => removeFormation(i)}
              data-testid={`cv-remove-formation-${i}`}
              className="w-6 h-6 rounded-lg flex items-center justify-center text-[#64748b] hover:text-[#ff6b6b] hover:bg-[#ff6b6b]/10 transition-colors shrink-0"
            >
              <X size={12} />
            </button>
          </div>
        ))}

        {showFormForm && (
          <div className="bg-[#0f0e17] border border-[#20c997]/20 rounded-xl p-3 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <input
                value={newForm.diploma}
                onChange={e => setNewForm(p => ({ ...p, diploma: e.target.value }))}
                placeholder="Diplôme / Certification"
                data-testid="cv-form-diploma-input"
                className="bg-white/5 border border-white/10 text-white rounded-lg px-2.5 py-2 text-xs focus:outline-none focus:border-[#20c997]/50"
              />
              <input
                value={newForm.school}
                onChange={e => setNewForm(p => ({ ...p, school: e.target.value }))}
                placeholder="Établissement"
                data-testid="cv-form-school-input"
                className="bg-white/5 border border-white/10 text-white rounded-lg px-2.5 py-2 text-xs focus:outline-none focus:border-[#20c997]/50"
              />
            </div>
            <input
              value={newForm.year}
              onChange={e => setNewForm(p => ({ ...p, year: e.target.value }))}
              placeholder="Année (ex: 2022)"
              data-testid="cv-form-year-input"
              className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-2.5 py-2 text-xs focus:outline-none focus:border-[#20c997]/50"
            />
            <textarea
              value={newForm.description}
              onChange={e => setNewForm(p => ({ ...p, description: e.target.value }))}
              placeholder="Description (optionnelle)"
              rows={2}
              data-testid="cv-form-description-input"
              className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-2.5 py-2 text-xs focus:outline-none focus:border-[#20c997]/50 resize-none"
            />
            <div className="flex gap-2">
              <button
                onClick={addFormation}
                data-testid="cv-form-add-confirm-btn"
                className="flex-1 py-2 rounded-lg bg-[#20c997] text-[#0f0e17] text-xs font-bold hover:opacity-90 transition-opacity"
              >
                Ajouter
              </button>
              <button
                onClick={() => { setShowFormForm(false); setNewForm({ diploma: '', school: '', year: '', description: '' }); }}
                className="px-3 py-2 rounded-lg border border-white/10 text-[#a0a0ab] text-xs hover:text-white transition-colors"
              >
                Annuler
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ── Competency Center (enrichi) ────────────────────────────────────────────────
function CompetencySection() {
  const navigate = useNavigate();
  const [competencies, setCompetencies] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedDomains, setExpandedDomains] = useState({});

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/courses/competencies`, { withCredentials: true }),
      axios.get(`${API}/certifications`, { withCredentials: true }),
    ])
      .then(([compRes, certRes]) => {
        setCompetencies(compRes.data.competencies || []);
        setCertifications(certRes.data.certifications || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="animate-shimmer h-28 rounded-2xl" />;

  const DOMAIN_THRESHOLD = 20;
  const shouldGroup = competencies.length > DOMAIN_THRESHOLD;

  // Simple domain grouping par mot-clé dans le nom
  const grouped = {};
  competencies.forEach(comp => {
    const domain = shouldGroup
      ? (comp.name.split(' ')[0] || 'Général')
      : 'all';
    if (!grouped[domain]) grouped[domain] = [];
    grouped[domain].push(comp);
  });

  return (
    <div className="space-y-4">
      {/* Compétences */}
      {competencies.length === 0 ? (
        <div className="kurso-card p-5 text-center">
          <Target size={24} className="text-[#a0a0ab] mx-auto mb-2" />
          <p className="text-[#a0a0ab] text-sm">Aucune compétence validée pour le moment.</p>
          <p className="text-xs text-[#a0a0ab] mt-1">
            Commencez un cours et alignez-le sur des référentiels.
          </p>
        </div>
      ) : (
        Object.entries(grouped).map(([domain, comps]) => {
          const isExpanded = expandedDomains[domain] !== false;
          return (
            <div key={domain}>
              {shouldGroup && (
                <button
                  onClick={() => setExpandedDomains(p => ({ ...p, [domain]: !isExpanded }))}
                  className="w-full flex items-center justify-between text-left mb-2"
                >
                  <span className="text-xs font-bold text-[#a0a0ab] uppercase tracking-wider">{domain}</span>
                  {isExpanded ? <ChevronUp size={12} className="text-[#64748b]" /> : <ChevronDown size={12} className="text-[#64748b]" />}
                </button>
              )}
              {(!shouldGroup || isExpanded) && (
                <div className="space-y-3">
                  {comps.map(comp => {
                    const pct = comp.total > 0 ? Math.round((comp.completed / comp.total) * 100) : 0;
                    const levelLabel = pct >= 80 ? 'Expert' : pct >= 50 ? 'Intermédiaire' : pct > 0 ? 'Débutant' : 'Non commencé';
                    const levelColor = pct >= 80 ? '#20c997' : pct >= 50 ? '#3b82f6' : pct > 0 ? '#ffc107' : '#64748b';
                    return (
                      <div key={comp.name} data-testid={`competency-${comp.name}`} className="kurso-card p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-lg bg-[#ff6b6b]/10 flex items-center justify-center shrink-0">
                              <Target size={12} className="text-[#ff6b6b]" />
                            </div>
                            <span className="text-white text-sm font-semibold">{comp.name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ color: levelColor, backgroundColor: `${levelColor}15`, border: `1px solid ${levelColor}30` }}>
                              {levelLabel}
                            </span>
                            <span className="text-xs text-[#a0a0ab]">{pct}%</span>
                          </div>
                        </div>
                        <div className="progress-bar-bg h-1.5 mb-2">
                          <div className="progress-bar-fill h-1.5 transition-all duration-700" style={{ width: `${pct}%` }} />
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex flex-wrap gap-1.5">
                            {(comp.courses || []).slice(0, 3).map(c => (
                              <button
                                key={c.id}
                                onClick={() => navigate(`/course/${c.id}`)}
                                className={`text-xs px-2 py-0.5 rounded-full border transition-colors ${
                                  c.completed
                                    ? 'bg-[#20c997]/10 border-[#20c997]/20 text-[#20c997]'
                                    : 'bg-white/5 border-white/10 text-[#a0a0ab] hover:text-white'
                                }`}
                              >
                                {c.completed ? '✓ ' : ''}{c.title.slice(0, 20)}{c.title.length > 20 ? '…' : ''}
                              </button>
                            ))}
                          </div>
                          <span className="text-[10px] text-[#64748b]">{comp.completed}/{comp.total} cours</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })
      )}

      {/* Certifications */}
      {certifications.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-heading text-base font-semibold text-white flex items-center gap-2">
            <Award size={15} className="text-[#ffc107]" /> Certifications
          </h3>
          {certifications.map((cert, i) => {
            const dateStr = cert.completed_at
              ? new Date(cert.completed_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })
              : '';
            const verifyUrl = `${window.location.origin}/certifications/verify/${cert.verification_hash}`;
            return (
              <div key={i} className="kurso-card p-4 flex items-center gap-3" data-testid={`cert-${i}`}>
                <div className="w-9 h-9 rounded-xl bg-[#ffc107]/10 flex items-center justify-center shrink-0">
                  <Award size={15} className="text-[#ffc107]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{cert.course_title}</p>
                  {dateStr && <p className="text-[#64748b] text-xs mt-0.5">{dateStr}</p>}
                </div>
                <a
                  href={verifyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-[#20c997] hover:opacity-80 flex items-center gap-1 transition-opacity shrink-0"
                  data-testid={`cert-verify-${i}`}
                >
                  <CheckCircle size={11} /> Vérifier
                </a>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Analyse de poste ────────────────────────────────────────────────────────────
function GapAnalysisSection() {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);

  useEffect(() => {
    if (historyLoaded) return;
    axios.get(`${API}/users/gap-analyses`, { withCredentials: true })
      .then(r => setHistory(r.data.analyses || []))
      .catch(() => {})
      .finally(() => setHistoryLoaded(true));
  }, [historyLoaded]);

  const handleAnalyze = async () => {
    if (!text.trim()) { toast.error('Collez une fiche de poste'); return; }
    setLoading(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/users/gap-analysis`, { text }, { withCredentials: true });
      setResult(res.data);
      toast.success(`Analyse terminée — ${res.data.credits_used} crédits utilisés`);
      setHistoryLoaded(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'analyse');
    } finally {
      setLoading(false);
    }
  };

  const priorityColor = { haute: '#ff6b6b', moyenne: '#ffc107', faible: '#20c997' };
  const priorityLabel = { haute: 'Haute', moyenne: 'Moyenne', faible: 'Faible' };

  return (
    <div className="space-y-5">
      <div>
        <h3 className="font-heading text-base font-semibold text-white flex items-center gap-2 mb-1">
          <Briefcase size={15} className="text-[#3b82f6]" /> Analyse de poste IA
        </h3>
        <p className="text-xs text-[#a0a0ab]">
          Collez une offre d'emploi pour analyser vos gaps de compétences. Coût : 3 crédits.
        </p>
      </div>

      <div className="space-y-3">
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder="Collez ici la fiche de poste ou l'offre d'emploi à analyser…"
          className="w-full bg-[#0f0e17] border border-[#2a2935] text-white text-sm rounded-2xl px-4 py-3 outline-none focus:border-[#3b82f6]/40 transition-colors resize-none min-h-[160px]"
          data-testid="gap-analysis-textarea"
        />
        <button
          onClick={handleAnalyze}
          disabled={loading || !text.trim()}
          className="flex items-center gap-2 bg-[#3b82f6] text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:opacity-90 disabled:opacity-50 transition-opacity"
          data-testid="gap-analysis-btn"
        >
          {loading ? <RefreshCw size={14} className="animate-spin" /> : <Briefcase size={14} />}
          {loading ? 'Analyse en cours…' : 'Analyser (3 crédits)'}
        </button>
      </div>

      {/* Résultat */}
      {result && (
        <div className="space-y-4 animate-fade-up" data-testid="gap-analysis-result">
          {/* Score */}
          <div className="kurso-card p-4 flex items-center gap-4">
            <div className="relative w-14 h-14 shrink-0">
              <svg viewBox="0 0 36 36" className="rotate-[-90deg] w-14 h-14">
                <circle cx="18" cy="18" r="15.9" fill="none" stroke="#2a2935" strokeWidth="3" />
                <circle
                  cx="18" cy="18" r="15.9" fill="none"
                  stroke={result.match_score >= 70 ? '#20c997' : result.match_score >= 40 ? '#ffc107' : '#ff6b6b'}
                  strokeWidth="3"
                  strokeDasharray={`${result.match_score} ${100 - result.match_score}`}
                  strokeLinecap="round"
                />
              </svg>
              <span className="absolute inset-0 flex items-center justify-center text-white text-sm font-bold">
                {result.match_score}%
              </span>
            </div>
            <div>
              <p className="text-white font-semibold text-sm">Score de correspondance</p>
              <p className="text-[#a0a0ab] text-xs mt-0.5 leading-relaxed">{result.summary}</p>
            </div>
          </div>

          {/* Compétences acquises */}
          {result.matching_skills?.length > 0 && (
            <div className="kurso-card p-4">
              <p className="text-xs font-bold text-[#20c997] uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <CheckCircle size={11} /> Compétences correspondantes
              </p>
              <div className="flex flex-wrap gap-2">
                {result.matching_skills.map((sk, i) => (
                  <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-[#20c997]/10 border border-[#20c997]/20 text-[#20c997]">
                    {sk}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Gaps */}
          {result.gaps?.length > 0 && (
            <div className="kurso-card p-4">
              <p className="text-xs font-bold text-[#ff6b6b] uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <AlertCircle size={11} /> Gaps à combler
              </p>
              <div className="space-y-2.5">
                {result.gaps.map((gap, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span
                      className="text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 mt-0.5"
                      style={{
                        color: priorityColor[gap.priority] || '#64748b',
                        backgroundColor: `${priorityColor[gap.priority] || '#64748b'}15`,
                        border: `1px solid ${priorityColor[gap.priority] || '#64748b'}30`,
                      }}
                    >
                      {priorityLabel[gap.priority] || gap.priority}
                    </span>
                    <div>
                      <p className="text-white text-sm font-medium">{gap.skill}</p>
                      {gap.suggestion && (
                        <p className="text-[#a0a0ab] text-xs mt-0.5 leading-relaxed">{gap.suggestion}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Historique */}
      {history.length > 0 && !result && (
        <div>
          <p className="text-xs font-bold text-[#a0a0ab] uppercase tracking-wider mb-2">Analyses récentes</p>
          <div className="space-y-2">
            {history.slice(0, 3).map((h, i) => (
              <div key={i} className="kurso-card px-4 py-3 flex items-center gap-3" data-testid={`gap-history-${i}`}>
                <div className="w-8 h-8 rounded-lg bg-[#3b82f6]/10 flex items-center justify-center shrink-0">
                  <Briefcase size={13} className="text-[#3b82f6]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-xs truncate">{h.job_text_preview}</p>
                  <p className="text-[#64748b] text-[10px] mt-0.5">
                    Score: {h.result?.match_score ?? '—'}% · {new Date(h.created_at).toLocaleDateString('fr-FR')}
                  </p>
                </div>
                <button
                  onClick={() => setResult(h.result)}
                  className="text-xs text-[#3b82f6] hover:opacity-80 transition-opacity"
                >
                  Voir
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Profil Public & Export CV ───────────────────────────────────────────────────
function PublicProfileSection() {
  const [slug, setSlug] = useState('');
  const [newSlug, setNewSlug] = useState('');
  const [visibility, setVisibility] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exportModal, setExportModal] = useState(false);
  const [exporting, setExporting] = useState('');

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/users/me/slug`, { withCredentials: true }),
      axios.get(`${API}/users/visibility`, { withCredentials: true }),
    ])
      .then(([slugRes, visRes]) => {
        setSlug(slugRes.data.slug || '');
        setNewSlug(slugRes.data.slug || '');
        setVisibility(visRes.data || {});
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSaveSlug = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/users/me/slug`, { slug: newSlug }, { withCredentials: true });
      setSlug(res.data.slug);
      setNewSlug(res.data.slug);
      toast.success(`Profil public activé : kurso.app/${res.data.slug}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la création du slug');
    } finally {
      setSaving(false);
    }
  };

  const handleVisibilityToggle = async (field, value) => {
    try {
      const res = await axios.patch(`${API}/users/visibility`, { [field]: value }, { withCredentials: true });
      setVisibility(res.data);
    } catch {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const res = await axios.post(`${API}/cv/export`, { format }, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `cv-${format}-kurso.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('CV téléchargé');
      setExportModal(false);
    } catch {
      toast.error('Erreur lors de l\'export PDF');
    } finally {
      setExporting('');
    }
  };

  if (loading) return <div className="animate-shimmer h-28 rounded-2xl" />;

  const profileUrl = slug ? `${window.location.origin}/${slug}` : null;

  return (
    <div className="space-y-5">
      {/* URL publique */}
      <div className="kurso-card p-4 space-y-3">
        <h3 className="text-white font-semibold text-sm flex items-center gap-2">
          <Globe size={14} className="text-[#20c997]" /> Votre URL publique
        </h3>
        {profileUrl ? (
          <div className="flex items-center gap-2 bg-[#0f0e17] border border-[#20c997]/20 rounded-xl px-3 py-2">
            <span className="text-[#20c997] text-sm flex-1 truncate">{profileUrl}</span>
            <a href={profileUrl} target="_blank" rel="noopener noreferrer" className="text-[#64748b] hover:text-white transition-colors">
              <Share2 size={13} />
            </a>
          </div>
        ) : (
          <p className="text-xs text-[#64748b]">Choisissez un slug pour activer votre profil public.</p>
        )}
        <div className="flex gap-2">
          <input
            value={newSlug}
            onChange={e => setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            className="flex-1 bg-[#0f0e17] border border-[#2a2935] text-white text-sm rounded-xl px-3 py-2 outline-none focus:border-[#20c997]/30 transition-colors"
            placeholder="votre-slug"
            data-testid="slug-input"
          />
          <button
            onClick={handleSaveSlug}
            disabled={saving || !newSlug}
            className="flex items-center gap-1.5 bg-[#20c997] text-[#0f0e17] px-4 py-2 rounded-xl text-sm font-bold hover:opacity-90 disabled:opacity-50 transition-opacity"
            data-testid="slug-save-btn"
          >
            {saving ? <RefreshCw size={12} className="animate-spin" /> : <CheckCircle size={12} />}
            Activer
          </button>
        </div>
      </div>

      {/* Visibilité */}
      {slug && (
        <div className="kurso-card p-4 space-y-3">
          <h3 className="text-white font-semibold text-sm flex items-center gap-2">
            <Eye size={14} className="text-[#a78bfa]" /> Contrôle de visibilité
          </h3>
          <p className="text-xs text-[#64748b]">Les compétences validées et certifications sont toujours visibles.</p>
          {[
            { field: 'is_public', label: 'Profil public activé', desc: 'Votre profil est accessible sans connexion', icon: Globe },
            { field: 'show_courses', label: 'Cours complétés', desc: 'Afficher la liste de vos formations', icon: BookOpen },
            { field: 'show_badges', label: 'Badges et collection', desc: 'Afficher vos badges Kurso', icon: Award },
            { field: 'show_xp', label: 'Niveau XP', desc: 'Afficher votre progression XP', icon: Zap },
          ].map(({ field, label, desc, icon: Icon }) => {
            const isOn = field === 'is_public' ? visibility.profile_public : visibility.visibility?.[field.replace('show_', 'show_')];
            const realKey = field === 'is_public' ? 'is_public' : field;
            const current = field === 'is_public' ? visibility.profile_public : visibility.visibility?.[field];
            return (
              <div key={field} className="flex items-start gap-3 py-2 border-b border-[#2a2935] last:border-0">
                <div className="w-7 h-7 rounded-lg bg-[#16161e] flex items-center justify-center shrink-0">
                  <Icon size={12} className="text-[#64748b]" />
                </div>
                <div className="flex-1">
                  <p className="text-white text-sm font-medium">{label}</p>
                  <p className="text-[#64748b] text-xs">{desc}</p>
                </div>
                <button
                  onClick={() => handleVisibilityToggle(realKey, !current)}
                  className={`w-11 h-6 rounded-full transition-all relative shrink-0 ${current ? 'bg-[#20c997]' : 'bg-[#2a2935]'}`}
                  data-testid={`visibility-toggle-${field}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${current ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Export CV */}
      <div className="kurso-card p-4 space-y-3">
        <h3 className="text-white font-semibold text-sm flex items-center gap-2">
          <Download size={14} className="text-[#ff6b6b]" /> Exporter mon CV
        </h3>
        <p className="text-xs text-[#64748b]">Générez un PDF professionnel avec vos compétences et QR codes de vérification.</p>
        <button
          onClick={() => setExportModal(true)}
          className="flex items-center gap-2 bg-[#ff6b6b] text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:opacity-90 transition-opacity"
          data-testid="cv-export-btn"
        >
          <FileText size={14} /> Exporter mon CV
        </button>
      </div>

      {/* Modal Export Format */}
      {exportModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setExportModal(false)}>
          <div className="bg-[#16161e] border border-[#2a2935] rounded-2xl p-6 max-w-sm w-full space-y-4" onClick={e => e.stopPropagation()}>
            <div>
              <h3 className="text-white font-bold text-lg font-heading">Choisir le format</h3>
              <p className="text-[#64748b] text-xs mt-1">Les deux formats incluent vos compétences Kurso vérifiables.</p>
            </div>
            <div className="space-y-3">
              <button
                onClick={() => handleExport('synthese')}
                disabled={!!exporting}
                className="w-full text-left bg-[#0f0e17] border border-[#2a2935] rounded-xl p-4 hover:border-[#20c997]/30 transition-all group"
                data-testid="export-synthese-btn"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#20c997]/10 flex items-center justify-center">
                    {exporting === 'synthese' ? <RefreshCw size={16} className="text-[#20c997] animate-spin" /> : <QrCode size={16} className="text-[#20c997]" />}
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">Format Synthèse</p>
                    <p className="text-[#64748b] text-xs">1 page — Compétences + certifications + QR codes</p>
                  </div>
                </div>
              </button>
              <button
                onClick={() => handleExport('complet')}
                disabled={!!exporting}
                className="w-full text-left bg-[#0f0e17] border border-[#2a2935] rounded-xl p-4 hover:border-[#ff6b6b]/30 transition-all"
                data-testid="export-complet-btn"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#ff6b6b]/10 flex items-center justify-center">
                    {exporting === 'complet' ? <RefreshCw size={16} className="text-[#ff6b6b] animate-spin" /> : <FileText size={16} className="text-[#ff6b6b]" />}
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">Format CV Complet</p>
                    <p className="text-[#64748b] text-xs">1-2 pages — Expériences + formations + compétences Kurso</p>
                  </div>
                </div>
              </button>
            </div>
            <button onClick={() => setExportModal(false)} className="w-full py-2 text-[#64748b] text-sm hover:text-white transition-colors">
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function Profile() {
  const { t } = useTranslation();
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const { reopenBanner, prefs } = useCookieConsent();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('stats');
  const [bilan, setBilan] = useState(null);
  const [exportHistory, setExportHistory] = useState([]);

  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');
  const [showPwForm, setShowPwForm] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);

  const handleLogout = () => {
    navigate('/');
    toast.success(t('nav.logoutToast'));
    logout();
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [profileRes, bilanRes, exportsRes] = await Promise.all([
          axios.get(`${API}/users/profile`, { withCredentials: true }),
          axios.get(`${API}/bilan`, { withCredentials: true }),
          axios.get(`${API}/export/history`, { withCredentials: true }).catch(() => ({ data: { exports: [] } })),
        ]);
        setProfile(profileRes.data);
        setBilan(bilanRes.data);
        setExportHistory(exportsRes.data.exports || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwError('');
    setPwSuccess('');
    if (pwForm.new_password.length < 6) { setPwError(t('profile.passwordMinError')); return; }
    if (pwForm.new_password !== pwForm.confirm_password) { setPwError(t('profile.passwordMismatch')); return; }
    setPwLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: pwForm.current_password || undefined,
        new_password: pwForm.new_password,
      }, { withCredentials: true });
      setPwSuccess(t('profile.passwordChanged'));
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
      setShowPwForm(false);
    } catch (err) {
      setPwError(err.response?.data?.detail || t('profile.passwordChangeError'));
    } finally {
      setPwLoading(false);
    }
  };

  const xp = profile?.xp || 0;
  const level = getLevel(xp);
  const levelIdx = LEVELS.findIndex(l => l.min === level.min);
  const nextLevel = LEVELS[levelIdx + 1];
  const xpForCurrent = level.min;
  const xpForNext = nextLevel?.min || xp;
  const progressPct = nextLevel ? ((xp - xpForCurrent) / (xpForNext - xpForCurrent)) * 100 : 100;

  const planLabels = {
    free:          { label: t('profile.planFree'),      color: 'text-[#e8e8e8]',   bg: 'bg-[#a0a0ab]/10' },
    solo:          { label: t('profile.soloPlan'),      color: 'text-[#ff6b6b]',   bg: 'bg-[#ff6b6b]/10' },
    famille:       { label: t('profile.famillePlan'),   color: 'text-[#20c997]',   bg: 'bg-[#20c997]/10' },
    organisation:  { label: t('profile.orgPlan'),       color: 'text-[#ffc107]',   bg: 'bg-[#ffc107]/10' },
    createur_pro:  { label: t('profile.createurPlan'),  color: 'text-[#ff6b6b]',   bg: 'bg-[#ff6b6b]/10' },
  };
  const planInfo = planLabels[profile?.plan || 'free'];

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 pt-6 pb-4 space-y-6">

        {/* Header */}
        <div className="animate-fade-up">
          <h1 className="font-heading text-2xl font-bold text-white mb-4">{t('profile.title')}</h1>
          {loading ? (
            <div className="animate-shimmer h-40 rounded-2xl" />
          ) : (
            <div className="kurso-card p-6">
              <div className="flex items-start gap-4 mb-5">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#ff6b6b]/20 to-[#ffc107]/20 border border-white/10 flex items-center justify-center">
                  <span className="font-heading font-bold text-2xl text-white">
                    {profile?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                </div>
                <div className="flex-1">
                  <h2 className="font-heading font-bold text-white text-xl">{profile?.name}</h2>
                  <p className="text-[#e8e8e8] text-sm">{profile?.email}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${planInfo?.bg} ${planInfo?.color}`}>
                      {planInfo?.label}
                    </span>
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full border border-white/10" style={{ color: level.color }}>
                      {t(`profile.${level.label}`)}
                    </span>
                  </div>
                </div>
              </div>
              {/* XP Bar */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="flex items-center gap-1.5 text-sm">
                    <Zap size={14} className="text-[#ffc107]" />
                    <span className="text-white font-semibold">{xp} XP</span>
                  </span>
                  {nextLevel && (
                    <span className="text-xs text-[#e8e8e8]">
                      {t('profile.xpFor', { xp: nextLevel.min - xp, level: t(`profile.${nextLevel.label}`) })}
                    </span>
                  )}
                </div>
                <div className="progress-bar-bg h-2.5">
                  <div className="progress-bar-fill h-2.5" style={{ width: `${Math.min(100, progressPct)}%` }} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Section toggle */}
        <div className="flex gap-1 bg-white/4 p-1 rounded-xl overflow-x-auto" data-testid="profile-section-tabs">
          {[
            { id: 'stats',        label: 'Statistiques' },
            { id: 'competencies', label: 'Compétences' },
            { id: 'gap_analysis', label: 'Analyse poste' },
            { id: 'public',       label: 'Profil public' },
            { id: 'cv',           label: 'CV Complet' },
          ].map(sec => (
            <button
              key={sec.id}
              onClick={() => setActiveSection(sec.id)}
              data-testid={`profile-tab-${sec.id}`}
              className={`flex-1 whitespace-nowrap py-2 px-2 rounded-lg text-xs font-medium transition-all ${
                activeSection === sec.id
                  ? 'bg-[#ff6b6b] text-white'
                  : 'text-[#a0a0ab] hover:text-white'
              }`}
            >
              {sec.label}
            </button>
          ))}
        </div>

        {/* ── Mes Compétences ────────────────────────────────────── */}
        {activeSection === 'competencies' && (
          <div className="animate-fade-up space-y-4">
            <div>
              <h2 className="font-heading text-lg font-semibold text-white flex items-center gap-2">
                <Target size={16} className="text-[#ff6b6b]" /> Mes Compétences
              </h2>
              <p className="text-xs text-[#a0a0ab] mt-0.5">
                Vos compétences validées par référentiel officiel, avec barres de progression et certifications.
              </p>
            </div>
            <CompetencySection />
          </div>
        )}

        {/* ── Analyse de poste ────────────────────────────────────── */}
        {activeSection === 'gap_analysis' && (
          <div className="animate-fade-up">
            <GapAnalysisSection />
          </div>
        )}

        {/* ── Profil public & Export CV ──────────────────────────── */}
        {activeSection === 'public' && (
          <div className="animate-fade-up">
            <PublicProfileSection />
          </div>
        )}

        {/* ── CV Complet ──────────────────────────────────────────── */}
        {activeSection === 'cv' && (
          <div className="animate-fade-up">
            <CVDataSection />
          </div>
        )}

        {/* ── Statistiques ───────────────────────────────────────── */}
        {activeSection === 'stats' && (
          <>
            {/* Stats grid */}
            {!loading && (
              <div className="grid grid-cols-3 gap-3 animate-fade-up" style={{ animationDelay: '0.1s' }}>
                {[
                  { icon: BookOpen, value: profile?.course_count || 0,    label: t('profile.statCourses'),   color: '#ff6b6b' },
                  { icon: Trophy,   value: profile?.completed_count || 0, label: t('profile.statCompleted'), color: '#20c997' },
                  { icon: Flame,    value: profile?.streak || 0,          label: t('profile.statStreak'),    color: '#ff6b6b' },
                ].map((stat, i) => (
                  <div key={i} className="kurso-card p-4 text-center">
                    <stat.icon size={20} className="mx-auto mb-2" style={{ color: stat.color }} />
                    <div className="font-heading font-bold text-white text-xl">{stat.value}</div>
                    <div className="text-xs text-[#e8e8e8]">{stat.label}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Badges */}
            <div className="animate-fade-up" style={{ animationDelay: '0.2s' }}>
              <h2 className="font-heading text-lg font-semibold text-white mb-3">{t('profile.badges')}</h2>
              {loading ? (
                <div className="animate-shimmer h-32 rounded-2xl" />
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(BADGE_META).map(([id, meta]) => {
                    const earned = profile?.badges?.includes(id);
                    return (
                      <div key={id} className={`kurso-card p-4 flex items-center gap-3 ${earned ? '' : 'opacity-40'}`} data-testid={`badge-${id}`}>
                        <div
                          className="w-10 h-10 rounded-2xl flex items-center justify-center shrink-0"
                          style={{ backgroundColor: `${meta.color}15`, border: earned ? `1px solid ${meta.color}40` : 'none' }}
                        >
                          <meta.icon size={18} style={{ color: meta.color }} />
                        </div>
                        <div className="min-w-0">
                          <p className={`text-sm font-medium ${earned ? 'text-white' : 'text-[#e8e8e8]'}`}>
                            {t(`badges.${id}`)}
                          </p>
                          <p className="text-xs text-[#e8e8e8]">{t(`badges.${id}_desc`)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Bilan */}
            <div className="animate-fade-up" style={{ animationDelay: '0.25s' }}>
              <h2 className="font-heading text-lg font-semibold text-white mb-3 flex items-center gap-2">
                <ClipboardList size={16} className="text-[#20c997]" /> {t('profile.bilanSection')}
              </h2>
              <button
                onClick={() => navigate('/bilan')}
                className={`w-full kurso-card p-4 flex items-center gap-3 text-left hover:-translate-y-0.5 transition-transform ${bilan?.completed ? 'border-[#20c997]/20' : 'border-[#ffc107]/20'}`}
                data-testid="profile-bilan-btn"
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${bilan?.completed ? 'bg-[#20c997]/10' : 'bg-[#ffc107]/10'}`}>
                  {bilan?.completed
                    ? <CheckCircle size={18} className="text-[#20c997]" />
                    : <ClipboardList size={18} className="text-[#ffc107]" />
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium text-sm">
                    {bilan?.completed ? t('profile.bilanDone') : t('profile.bilanDo')}
                  </p>
                  <p className="text-[#e8e8e8] text-xs">
                    {bilan?.completed
                      ? `${bilan.steps_completed?.length || 0} étapes`
                      : t('profile.bilanStartPrompt')}
                  </p>
                </div>
                <ChevronRight size={16} className="text-[#e8e8e8] shrink-0" />
              </button>
              {bilan?.completed && (
                <button
                  onClick={async () => {
                    const shareJeSais = !bilan.privacy?.share_je_sais;
                    const shareAction = bilan.privacy?.share_action_plan || false;
                    await axios.put(`${API}/bilan/privacy`, { share_je_sais: shareJeSais, share_action_plan: shareAction }, { withCredentials: true });
                    setBilan(prev => ({ ...prev, privacy: { share_je_sais: shareJeSais, share_action_plan: shareAction } }));
                  }}
                  className="mt-2 text-xs text-[#e8e8e8] hover:text-white flex items-center gap-1.5 transition-colors"
                  data-testid="toggle-bilan-privacy-btn"
                >
                  <Shield size={12} className="text-[#20c997]" />
                  {t('profile.bilanEditPrivacy')}
                </button>
              )}
            </div>

            {/* Cookies */}
            <div className="animate-fade-up kurso-card p-5" style={{ animationDelay: '0.35s' }}>
              <div className="flex items-center gap-2 mb-3">
                <Cookie size={16} className="text-[#ff6b6b]" />
                <h3 className="font-heading font-semibold text-white">{t('profile.privacySection')}</h3>
              </div>
              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between">
                  <span className="text-[#a0a0ab] text-sm">{t('profile.cookiesNecessary')}</span>
                  <span className="text-xs text-[#06d6a0] font-medium">{t('profile.alwaysOn')}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[#a0a0ab] text-sm">{t('profile.cookiesAnalyticsLabel')}</span>
                  <span className={`text-xs font-medium ${prefs?.analytics ? 'text-[#06d6a0]' : 'text-[#a0a0ab]'}`}>
                    {prefs?.analytics ? t('profile.cookiesEnabled') : t('profile.cookiesDisabled')}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[#a0a0ab] text-sm">{t('profile.cookiesMarketingLabel')}</span>
                  <span className={`text-xs font-medium ${prefs?.marketing ? 'text-[#06d6a0]' : 'text-[#a0a0ab]'}`}>
                    {prefs?.marketing ? t('profile.cookiesEnabled') : t('profile.cookiesDisabled')}
                  </span>
                </div>
              </div>
              <button onClick={reopenBanner} data-testid="profile-cookie-prefs-btn"
                className="w-full py-2.5 rounded-xl border border-white/10 text-[#e8e8e8] text-sm hover:bg-white/5 transition-colors">
                {t('profile.cookiesBtn')}
              </button>
            </div>

            {/* Security */}
            <div className="animate-fade-up kurso-card p-5" style={{ animationDelay: '0.4s' }}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Key size={16} className="text-[#ffc107]" />
                  <h3 className="font-heading font-semibold text-white">{t('profile.securitySection')}</h3>
                </div>
                <button
                  onClick={() => { setShowPwForm(!showPwForm); setPwError(''); setPwSuccess(''); }}
                  data-testid="toggle-change-password-btn"
                  className="text-xs text-[#a0a0ab] hover:text-[#ff6b6b] transition-colors"
                >
                  {showPwForm ? t('profile.cancelChange') : t('profile.changePasswordBtn')}
                </button>
              </div>
              {profile?.social_providers?.length > 0 && (
                <div className="mb-3 flex gap-2 flex-wrap">
                  {profile.social_providers.map(p => (
                    <span key={p} className="text-xs px-2 py-0.5 rounded-full bg-white/10 text-[#e8e8e8] capitalize">
                      {p === 'google' ? '🔵 Google' : p === 'microsoft' ? '🟦 Microsoft' : '🟦 Facebook'}
                    </span>
                  ))}
                </div>
              )}
              {pwSuccess && (
                <div className="text-[#06d6a0] text-sm bg-[#06d6a0]/10 rounded-xl px-3 py-2 mb-3 flex items-center gap-2">
                  <CheckCircle size={14} /> {pwSuccess}
                </div>
              )}
              {showPwForm && (
                <form onSubmit={handleChangePassword} className="space-y-3">
                  {(!profile?.social_providers?.length) && (
                    <div>
                      <label className="text-xs text-[#a0a0ab] mb-1 block">{t('profile.currentPasswordLabel')}</label>
                      <input
                        type="password" value={pwForm.current_password}
                        onChange={e => setPwForm(p => ({ ...p, current_password: e.target.value }))}
                        placeholder={t('profile.currentPasswordLabel')}
                        data-testid="current-password-input"
                        className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#ff6b6b]/50"
                      />
                    </div>
                  )}
                  <div>
                    <label className="text-xs text-[#a0a0ab] mb-1 block">{t('profile.newPasswordLabel')}</label>
                    <div className="relative">
                      <input
                        type={showNewPw ? 'text' : 'password'} value={pwForm.new_password}
                        onChange={e => setPwForm(p => ({ ...p, new_password: e.target.value }))}
                        placeholder={t('profile.passwordMinPlaceholder')} required minLength={6}
                        data-testid="new-password-input"
                        className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-3 py-2.5 pr-10 text-sm focus:outline-none focus:border-[#ff6b6b]/50"
                      />
                      <button type="button" onClick={() => setShowNewPw(!showNewPw)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#a0a0ab] hover:text-white">
                        {showNewPw ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-[#a0a0ab] mb-1 block">{t('profile.confirmPasswordLabel')}</label>
                    <input
                      type={showNewPw ? 'text' : 'password'} value={pwForm.confirm_password}
                      onChange={e => setPwForm(p => ({ ...p, confirm_password: e.target.value }))}
                      placeholder={t('profile.repeatPasswordPlaceholder')} required
                      data-testid="confirm-new-password-input"
                      className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#ff6b6b]/50"
                    />
                  </div>
                  {pwError && <p className="text-[#ff6b6b] text-xs">{pwError}</p>}
                  <button
                    type="submit" disabled={pwLoading}
                    data-testid="save-password-btn"
                    className="w-full py-2.5 rounded-xl bg-[#ff6b6b] text-white text-sm font-bold hover:bg-[#ff5555] disabled:opacity-50 transition-colors"
                  >
                    {pwLoading ? t('profile.savingBtn') : t('profile.savePasswordBtn')}
                  </button>
                </form>
              )}
            </div>

            {/* Export history */}
            <div className="animate-fade-up kurso-card p-5" style={{ animationDelay: '0.28s' }} data-testid="export-history-section">
              <h3 className="font-heading font-semibold text-white mb-3 flex items-center gap-2">
                <BookOpen size={16} className="text-[#ff6b6b]" /> {t('profile.myExports')}
              </h3>
              {exportHistory.length === 0 ? (
                <p className="text-[#a0a0ab] text-sm">{t('profile.noExports')}</p>
              ) : (
                <div className="space-y-2">
                  {exportHistory.map((exp, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                      <div>
                        <p className="text-sm text-white font-medium">{exp.course_title || 'Cours'}</p>
                        <p className="text-xs text-[#a0a0ab]">
                          {exp.format?.toUpperCase()} · {new Date(exp.created_at).toLocaleDateString('fr-FR')} · Plan {exp.plan}
                        </p>
                      </div>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-white/5 text-[#a0a0ab] uppercase">{exp.format}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Plan upgrade */}
            {profile?.plan === 'free' && (
              <div className="animate-fade-up kurso-card p-5 border-[#ff6b6b]/20" style={{ animationDelay: '0.3s' }}>
                <h3 className="font-heading font-semibold text-white mb-1">{t('profile.upgradeTitle')}</h3>
                <p className="text-[#e8e8e8] text-sm mb-3">{t('profile.upgradeDesc')}</p>
                <button onClick={() => navigate('/pricing')} className="btn-primary py-2.5 px-5 text-sm" data-testid="upgrade-plan-btn">
                  {t('profile.seeOffers')}
                </button>
              </div>
            )}
          </>
        )}

        {/* Logout */}
        <div className="animate-fade-up pb-4">
          <button
            onClick={handleLogout}
            data-testid="profile-logout-btn"
            className="w-full flex items-center justify-center gap-2.5 py-3 rounded-2xl border border-[#ff6b6b]/20 text-[#ff6b6b]/70 hover:text-[#ff6b6b] hover:bg-[#ff6b6b]/5 hover:border-[#ff6b6b]/40 transition-all duration-200 text-sm font-medium"
          >
            <LogOut size={16} />
            {t('nav.logout')}
          </button>
        </div>
      </div>
    </Layout>
  );
}
